/**
 * ProgressStepper — horizontal step indicator for the onboarding wizard.
 * Mobile: horizontally scrollable. Desktop: centered, full-width.
 */

import { Check } from 'lucide-react';
import type { OnboardingStep } from './types';

interface StepIndicatorProps {
  step: number;
  label: string;
  active: boolean;
  completed: boolean;
}

function StepIndicator({ step, label, active, completed }: StepIndicatorProps) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={[
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-all duration-200',
          active
            ? 'bg-blue-600 text-white ring-4 ring-blue-200 animate-pulse'
            : completed
              ? 'bg-green-500 text-white'
              : 'bg-gray-200 text-gray-500',
        ].join(' ')}
        aria-current={active ? 'step' : undefined}
      >
        {completed ? <Check className="h-4 w-4" aria-hidden="true" /> : step}
      </div>
      <span
        className={[
          'hidden whitespace-nowrap text-sm font-medium sm:block',
          active ? 'text-blue-700' : completed ? 'text-green-600' : 'text-gray-400',
        ].join(' ')}
      >
        {label}
      </span>
    </div>
  );
}

const STEP_LABELS: Record<number, string> = {
  1: 'Welcome',
  2: 'Profile',
  3: 'Interests',
  4: 'Tour',
  5: 'Next Steps',
};

interface ProgressStepperProps {
  currentStep: OnboardingStep;
}

export function ProgressStepper({ currentStep }: ProgressStepperProps) {
  return (
    <nav aria-label="Onboarding progress" className="overflow-x-auto border-b border-gray-100">
      <ol className="mx-auto flex min-w-max items-center justify-center gap-3 px-6 py-4">
        {([1, 2, 3, 4, 5] as const).map((step, index) => (
          <li key={step} className="flex items-center gap-3">
            <StepIndicator
              step={step}
              label={STEP_LABELS[step]}
              active={currentStep === step}
              completed={currentStep > step}
            />
            {index < 4 && (
              <div
                className={[
                  'h-0.5 w-6 shrink-0 transition-colors duration-300',
                  currentStep > step ? 'bg-green-400' : 'bg-gray-200',
                ].join(' ')}
                aria-hidden="true"
              />
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
